import React, { useEffect, useState } from "react";
import _ from "lodash";
import classNames from "classnames";
import { Pagination } from "react-bootstrap";
import { useWindowSize } from "react-use";
import { IPaginationBase } from "@models/interfaces/IPagination";

interface IPaginationComp {
  className?: string;
  maxItems?: number;
  pagination: IPaginationBase;
  onPageChanged?: (pageNo: number) => void;
}

const PaginationComp: React.FC<IPaginationComp> = (props: IPaginationComp) => {
  const [pagination, setPagination] = useState(props.pagination),
    [maxItems, setMaxItems] = useState(_.clamp(props.maxItems || 5, 5, 10)),
    { width: windowWidth } = useWindowSize(),
    classes = classNames(props.className);

  useEffect(() => {
    if (windowWidth < 768 && maxItems > 3) {
      setMaxItems(3);
    } else {
      setMaxItems(_.clamp(props.maxItems || 5, 5, 10));
    }
  }, [windowWidth]);

  useEffect(() => {
    setPagination(props.pagination);
  }, [props.pagination]);

  const lastPage = Math.ceil(pagination.totalCount / pagination.rowsPerPage),
    currentPage = _.clamp(pagination.page, 1, lastPage),
    pagesArr = [];

  // The first page needs to always exist.
  pagesArr.push(1);

  // The pages in between 1 and lastPage.
  let start = _.clamp(Math.ceil(currentPage - maxItems / 2), 2, lastPage),
    end = _.clamp(start + maxItems, start + 1, lastPage);

  const dStartEnd = maxItems - (end - start);

  if (_.inRange(start, 1, lastPage) && _.inRange(end, 1, lastPage + 1)) {
    for (let i = 0; i < dStartEnd; i++) {
      // Attempt to fill the remaining pages to fulfill maxItems
      const distFirstAndStart = start - 2,
        distEndAndLast = end - lastPage;

      if (distFirstAndStart && distFirstAndStart > distEndAndLast) {
        // There is more space at lower areas, expand it.
        start--;
      } else if (distEndAndLast && distEndAndLast > distFirstAndStart) {
        // There is more space at higher areas, expand it.
        end++;
      } else if (distEndAndLast === distFirstAndStart) {
        // There is equal space between lower and higher areas,
        // Ensure there's space and expand any of them with order from lower then higher.
        if (distFirstAndStart) {
          start--;
        } else if (distEndAndLast) {
          end++;
        }
      }
    }

    pagesArr.push(..._.range(start, end, 1));
  }

  // The last page also needs to exist.
  if (lastPage > 1) {
    pagesArr.push(lastPage);
  }

  /** Event handles */
  const handleOnPageChanged = (pageNo: number) => {
      if (props.onPageChanged && typeof props.onPageChanged === "function") {
        return props.onPageChanged(pageNo);
      }
    },
    onPrevClicked = () => {
      handleOnPageChanged(_.clamp(currentPage - 1, 1, lastPage));
    },
    onNextClicked = () => {
      handleOnPageChanged(_.clamp(currentPage + 1, 1, lastPage));
    },
    onPageClicked = (evt: React.MouseEvent<HTMLElement>) => {
      const elem = evt.target as HTMLElement,
        pageNo = parseInt(elem.dataset["page"]) || 0;

      handleOnPageChanged(_.clamp(pageNo, 1, lastPage));
    };

  return (
    <Pagination className={classes}>
      <Pagination.Prev onClick={onPrevClicked} />
      {pagesArr.map((pageIdx) => {
        return (
          <Pagination.Item key={pageIdx} onClick={onPageClicked} active={currentPage === pageIdx} data-page={pageIdx}>
            {pageIdx}
          </Pagination.Item>
        );
      })}
      <Pagination.Next onClick={onNextClicked} />
    </Pagination>
  );
};

export default PaginationComp;
